import type { CSSProperties } from "react";
import type { PublicProposal, ProposalTier } from "../types/proposals";
import type {
  ProposalTheme,
  ProposalSection,
  PresentationOptions,
} from "../types/proposalPresentation";

export const cents = (value: number | string | null | undefined) =>
  Math.round(Number(value || 0) * 100);
export const money = (value: number | string | null | undefined) =>
  (Number(value) || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

// Display only: the saved tier total remains the amount used for acceptance.
// All pricing inputs have two decimal places; calculate in integer units so
// original price and savings reconcile to the cent in both web and PDF views.
export function tierDiscount(
  proposal: Pick<PublicProposal, "total_squares" | "tax_enabled" | "tax_rate">,
  tier: ProposalTier | undefined,
) {
  if (!tier?.use_manual_pricing || tier.total_amount == null) return null;
  const price = Number(tier.manufacturer_product_details?.price_per_square);
  const squares = Number(proposal.total_squares);
  const rate = proposal.tax_enabled ? Number(proposal.tax_rate) : 0;
  const actual = Number(tier.total_amount);
  if (
    ![price, squares, rate, actual].every(Number.isFinite) ||
    price <= 0 ||
    squares <= 0 ||
    rate < 0 ||
    actual < 0
  )
    return null;
  const originalCents = Number(
    (BigInt(cents(price)) *
      BigInt(cents(squares)) *
      (10000n + BigInt(cents(rate))) +
      500000n) /
      1000000n,
  );
  const savingsCents = originalCents - cents(actual);
  return savingsCents > 0
    ? { original: originalCents / 100, savings: savingsCents / 100 }
    : null;
}

export function hasDistinctTierLabel(
  tier: Pick<ProposalTier, "name" | "tier_type_label">,
) {
  const normalize = (value: string) =>
    value.trim().replace(/\s+/g, " ").toLowerCase();
  const label = normalize(tier.tier_type_label || "");
  return Boolean(label && label !== normalize(tier.name));
}

export function themeStyle(
  brand: ProposalTheme,
  theme: "light" | "dark",
): CSSProperties {
  return {
    ...Object.fromEntries(
      Object.entries(brand[theme]).map(([key, value]) => [
        `--proposal-${key.replaceAll("_", "-")}`,
        value,
      ]),
    ),
    "--proposal-heading": `"${brand.heading_font}", Arial, sans-serif`,
    "--proposal-body": `"${brand.body_font}", Arial, sans-serif`,
  } as CSSProperties;
}

export function logoFor(brand: ProposalTheme, theme: "light" | "dark") {
  const keys = [
    `wide_${theme === "dark" ? "light" : "dark"}`,
    `square_${theme === "dark" ? "light" : "dark"}`,
  ];
  const matched = keys.map((key) => brand.logos[key]).find(Boolean);
  return {
    url:
      matched || brand.logos.wide_full_color || brand.logos.square_full_color,
    plate: !matched,
  };
}

export function requiredProducts(
  proposal: PublicProposal,
  tierId: number | null,
) {
  const products = new Map<
    number,
    NonNullable<
      PublicProposal["tiers"][number]["line_items"][number]["product_details"]
    >
  >();
  const tier = proposal.tiers.find((item) => item.id === tierId);
  for (const item of tier?.line_items || []) {
    if (item.product_details?.options.some((option) => option.is_active))
      products.set(item.product_details.id, item.product_details);
  }
  return [...products.values()];
}

export const requiredFeeCents = (proposal: Pick<PublicProposal, "optional_upgrades">) =>
  proposal.optional_upgrades.filter(item => item.is_required).reduce((sum, item) => sum + cents(item.total_price), 0);

export const packagePrice = (proposal: Pick<PublicProposal, "optional_upgrades">, tier: ProposalTier) =>
  (cents(tier.total_amount) + requiredFeeCents(proposal)) / 100;

export function totalCents(
  proposal: PublicProposal,
  tierId: number | null,
  upgrades: number[],
) {
  const tier = proposal.tiers.find((item) => item.id === tierId);
  return (
    cents(tier?.total_amount) +
    proposal.optional_upgrades
      .filter((item) => item.is_required || upgrades.includes(item.id))
      .reduce((sum, item) => sum + cents(item.total_price), 0)
  );
}

export function paymentSchedule(options: PresentationOptions, total: number) {
  const rows = options.milestones || [];
  const lastPayment = rows.reduce(
    (last, row, index) => (row.percent !== undefined ? index : last),
    -1,
  );
  let remaining = total;
  return rows.map((row, index) => {
    if (row.percent === undefined) return { ...row, amount: undefined };
    const amount =
      index === lastPayment
        ? remaining
        : Number(
            (BigInt(total) * BigInt(Math.round(Number(row.percent) * 100)) +
              5000n) /
              10000n,
          );
    remaining -= amount;
    return { ...row, amount };
  });
}

export function workflowSteps(
  proposal: PublicProposal,
  tierId: number | null,
): ProposalSection[] {
  const configured =
    proposal.presentation?.sections ||
    proposal.pages
      .filter((page) => page.is_enabled)
      .map((page) => ({
        id: String(page.id),
        type: page.page_type,
        title: page.title,
        intro: page.intro_text,
        content: page.content,
        image: page.custom_image,
      }));
  const steps: ProposalSection[] = [];
  for (const section of configured) {
    if (section.type === "authorization") {
      if (requiredProducts(proposal, tierId).length)
        steps.push({
          id: `${section.id}-choices`,
          type: "choices",
          title: "Your product choices",
        });
      if (proposal.optional_upgrades.length)
        steps.push({
          id: `${section.id}-upgrades`,
          type: "upgrades",
          title: "Add-ons & fees",
        });
      steps.push({ ...section, type: "investment", title: "Your investment" });
    } else steps.push(section);
  }
  if (
    !steps.some((step) => step.type === "quote_details") &&
    (proposal.tiers.length || proposal.service_configuration?.services?.length)
  ) {
    steps.push({
      id: "required-options",
      type: "quote_details",
      title: "Your options",
    });
  }
  if (!steps.some((step) => step.type === "investment")) {
    if (requiredProducts(proposal, tierId).length)
      steps.push({
        id: "required-choices",
        type: "choices",
        title: "Your product choices",
      });
    if (proposal.optional_upgrades.length)
      steps.push({
        id: "required-upgrades",
        type: "upgrades",
        title: "Add-ons & fees",
      });
    steps.push({
      id: "required-investment",
      type: "investment",
      title: "Your investment",
    });
  }
  steps.push({ id: "sign", type: "sign", title: "Sign & accept" });
  return steps;
}
